import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@material-ui/icons/Close";
import AddShoppingCartIcon from "@material-ui/icons/AddShoppingCart";
import { FiGift, FiSearch, FiX } from "react-icons/fi";
import { toast } from "react-toastify";

import v2_actionSugestoesMeias from "../actions/v2/produtos/v2_actionSugestoesMeias";
import v2_actionBuscarProduto from "../actions/v2/produtos/v2_actionBuscarProduto";
import v2_actionPrecosProdutos from "../actions/v2/produtos/v2_actionPrecosProdutos";
import { addToCart } from "../actions/index";
import Actions from "../constants/Actions";
import { carrinhoTemMeia } from "../utils/isProdutoMeia";
import { formatarCodigoFilial } from "../utils/functions";
import {
  collectProductImageUrls,
  getProductThumbUrl,
  SEM_IMAGEM,
} from "../utils/productImageUrl";
import useDebounce from "../utils/useDebounce";

const BRAND = "#1a1a2e";
const BRAND_LIGHT = "#e8eaf0";
const ACCENT = "#16a34a";
const ACCENT_HOVER = "#15803d";

const ProductThumb = ({ produto }) => {
  const candidates = collectProductImageUrls(produto);
  const initialSrc = getProductThumbUrl(produto, SEM_IMAGEM);

  const handleError = (e) => {
    const img = e.currentTarget;
    const nextIndex = Number(img.dataset.fallbackIndex || 0) + 1;
    if (nextIndex < candidates.length) {
      img.dataset.fallbackIndex = String(nextIndex);
      img.src = candidates[nextIndex];
      return;
    }
    img.onerror = null;
    img.src = SEM_IMAGEM;
  };

  return (
    <Box
      component="img"
      src={initialSrc}
      alt={produto.use_descricao || produto.name}
      data-fallback-index="0"
      onError={handleError}
      sx={{ width: "100%", height: "100%", objectFit: "contain" }}
    />
  );
};

const formatPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `R$ ${num.toFixed(2).replace(".", ",")}`;
};

/** Remove códigos numéricos finais e deixa o nome mais legível. */
const formatTituloMeia = (texto) => {
  if (!texto) return "Meia";
  return String(texto)
    .replace(/\s+\d{5,}(\s+\d{5,})*\s*$/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
};

const getDimensoes = (product) => {
  const d = product?.dimensions || {};
  return {
    length: d.length || 0,
    height: d.height || 0,
    width: d.width || 0,
  };
};

const buscarPrecoLoja = async ({ referencia, corProduto, tamanho, loja }) => {
  if (!referencia || !corProduto || !tamanho || !loja) return null;
  try {
    const res = await v2_actionPrecosProdutos(
      `?referencia=${encodeURIComponent(referencia)}&cor_produto=${encodeURIComponent(
        corProduto
      )}&tamanho=${encodeURIComponent(tamanho)}&loja=${encodeURIComponent(loja)}`
    );
    if (res.status === 200 && res.data?.preco !== undefined && res.data?.preco !== null) {
      return res.data.preco;
    }
  } catch (err) {
    console.error("Erro ao consultar preço da meia:", err);
  }
  return null;
};

const MeiaCard = ({ item, compact, addingKey, onAdicionar }) => {
  const produto = item.produto || item;
  const itemKey = `${produto.use_referencia}-${produto.use_cod_cor_produto}`;
  const isAdding = addingKey === itemKey;
  const precoIndisponivel =
    !produto.preco_carregando &&
    (produto.preco_loja === null || produto.preco_loja === undefined);
  const podeAdicionar = !produto.preco_carregando && !precoIndisponivel && !addingKey;
  const tamanho = Array.isArray(produto.grades) ? produto.grades[0] : null;
  const titulo = formatTituloMeia(produto.use_descricao || produto.name);

  if (compact) {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          alignItems: "stretch",
          borderRadius: 2,
          border: "1px solid",
          borderColor: isAdding ? ACCENT : "grey.200",
          overflow: "hidden",
          bgcolor: "#fff",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          opacity: addingKey && !isAdding ? 0.55 : 1,
          position: "relative",
          minHeight: 112,
        }}
      >
        {isAdding && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.75)",
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={26} sx={{ color: ACCENT }} />
          </Box>
        )}

        <Box
          sx={{
            width: 96,
            flexShrink: 0,
            bgcolor: "#fafafa",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 1,
            borderRight: "1px solid",
            borderColor: "grey.100",
          }}
        >
          <ProductThumb produto={produto} />
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            py: 1.25,
            pr: 1.25,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {produto.griffe && (
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                  color: "text.secondary",
                }}
              >
                {produto.griffe}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: "0.82rem",
                fontWeight: 600,
                lineHeight: 1.25,
                color: BRAND,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {titulo}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {tamanho && (
              <Chip
                size="small"
                label={tamanho}
                sx={{ height: 20, fontSize: "0.65rem", bgcolor: BRAND_LIGHT, color: BRAND }}
              />
            )}
            {produto.preco_carregando ? (
              <CircularProgress size={12} sx={{ color: BRAND }} />
            ) : precoIndisponivel ? (
              <Typography sx={{ fontSize: "0.72rem", color: "error.main", fontWeight: 600 }}>
                Sem preço
              </Typography>
            ) : (
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: BRAND }}>
                {formatPrice(produto.preco_loja)}
              </Typography>
            )}
          </Stack>

          <Button
            size="small"
            variant="contained"
            disabled={!podeAdicionar && !isAdding}
            startIcon={!isAdding ? <AddShoppingCartIcon style={{ fontSize: 15 }} /> : null}
            onClick={() => onAdicionar(item)}
            sx={{
              mt: "auto",
              alignSelf: "stretch",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.25,
              py: 0.7,
              minHeight: 36,
              bgcolor: ACCENT,
              boxShadow: "none",
              "&:hover": { bgcolor: ACCENT_HOVER, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "grey.200", color: "grey.500" },
            }}
          >
            {isAdding ? "Adicionando…" : "Adicionar"}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: "1px solid",
        borderColor: isAdding ? ACCENT : "grey.200",
        overflow: "hidden",
        bgcolor: "#fff",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
        opacity: addingKey && !isAdding ? 0.55 : 1,
        position: "relative",
        "&:hover": podeAdicionar
          ? {
              borderColor: BRAND,
              boxShadow: "0 8px 20px rgba(26,26,46,0.10)",
              transform: "translateY(-2px)",
            }
          : undefined,
      }}
    >
      {isAdding && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.75)",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={28} sx={{ color: ACCENT }} />
        </Box>
      )}

      <Box
        sx={{
          height: { sm: 110, md: 118 },
          bgcolor: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 1.25,
          borderBottom: "1px solid",
          borderColor: "grey.100",
        }}
      >
        <ProductThumb produto={produto} />
      </Box>

      <Box
        sx={{
          p: { sm: 1.25, md: 1.5 },
          display: "flex",
          flexDirection: "column",
          flex: 1,
          gap: 1,
        }}
      >
        <Box>
          {produto.griffe && (
            <Typography
              sx={{
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: "text.secondary",
                mb: 0.35,
              }}
            >
              {produto.griffe}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: { sm: "0.75rem", md: "0.8rem" },
              fontWeight: 600,
              lineHeight: 1.3,
              color: BRAND,
              height: "2.6em",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {titulo}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {tamanho && (
            <Chip
              size="small"
              label={`Tam. ${tamanho}`}
              sx={{ height: 22, fontSize: "0.68rem", bgcolor: BRAND_LIGHT, color: BRAND }}
            />
          )}
          {produto.use_referencia && (
            <Chip
              size="small"
              label={`Ref. ${produto.use_referencia}`}
              variant="outlined"
              sx={{ height: 22, fontSize: "0.68rem" }}
            />
          )}
        </Stack>

        <Box sx={{ mt: "auto", pt: 0.5 }}>
          <Box sx={{ minHeight: 28, mb: 1 }}>
            {produto.preco_carregando ? (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <CircularProgress size={14} sx={{ color: BRAND }} />
                <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                  Buscando preço…
                </Typography>
              </Stack>
            ) : precoIndisponivel ? (
              <Typography sx={{ fontSize: "0.8rem", color: "error.main", fontWeight: 600 }}>
                Preço indisponível
              </Typography>
            ) : (
              <Typography
                sx={{ fontSize: { sm: "0.95rem", md: "1.05rem" }, fontWeight: 800, color: BRAND }}
              >
                {formatPrice(produto.preco_loja)}
              </Typography>
            )}
          </Box>

          <Button
            fullWidth
            size="small"
            variant="contained"
            disabled={!podeAdicionar && !isAdding}
            startIcon={!isAdding ? <AddShoppingCartIcon style={{ fontSize: 16 }} /> : null}
            onClick={() => onAdicionar(item)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.5,
              py: 0.85,
              minHeight: 40,
              bgcolor: ACCENT,
              boxShadow: "none",
              "&:hover": { bgcolor: ACCENT_HOVER, boxShadow: "none" },
              "&.Mui-disabled": {
                bgcolor: "grey.200",
                color: "grey.500",
              },
            }}
          >
            {isAdding ? "Adicionando…" : "Adicionar"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const PAGE_SIZE = 8;

const enriquecerComPrecos = async (lista, loja) => {
  return Promise.all(
    lista.map(async (item) => {
      const produto = item.produto || item;
      const tamanho = Array.isArray(produto.grades) ? produto.grades[0] : null;
      const preco = await buscarPrecoLoja({
        referencia: produto.use_referencia,
        corProduto: produto.use_cod_cor_produto,
        tamanho,
        loja,
      });
      return {
        ...item,
        produto: {
          ...produto,
          preco_loja: preco,
          preco_carregando: false,
        },
      };
    })
  );
};

const marcarCarregandoPreco = (lista) =>
  lista.map((item) => {
    const produto = item.produto || item;
    return {
      ...item,
      produto: {
        ...produto,
        preco_loja: null,
        preco_carregando: true,
      },
    };
  });

const MeiaCardSkeleton = ({ compact }) => {
  if (compact) {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          minHeight: 112,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "grey.200",
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        <Skeleton variant="rectangular" width={96} height={112} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, py: 1.25, pr: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
          <Skeleton width="40%" height={12} />
          <Skeleton width="90%" height={16} />
          <Skeleton width="55%" height={16} />
          <Skeleton variant="rounded" width="100%" height={36} sx={{ mt: "auto", borderRadius: 1.25 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "grey.200",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Skeleton variant="rectangular" height={118} />
      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
        <Skeleton width="35%" height={12} />
        <Skeleton width="100%" height={16} />
        <Skeleton width="70%" height={16} />
        <Stack direction="row" spacing={0.75}>
          <Skeleton variant="rounded" width={56} height={22} />
          <Skeleton variant="rounded" width={72} height={22} />
        </Stack>
        <Skeleton width="45%" height={22} sx={{ mt: 0.5 }} />
        <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: 1.5 }} />
      </Box>
    </Box>
  );
};

const SugestaoMeiasResumo = () => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cartItems);
  const cepOrigemProduto = useSelector((state) => state.cepOrigemProduto);

  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPrecos, setLoadingPrecos] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [addingKey, setAddingKey] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const searchDebounced = useDebounce(searchInput.trim(), 400);
  const dismissedRef = useRef(false);

  const lojaCarrinho = useMemo(() => {
    const fromItem = cartItems.find((item) => item?.filial)?.filial;
    return fromItem || cepOrigemProduto || null;
  }, [cartItems, cepOrigemProduto]);

  const jaTemMeia = useMemo(() => carrinhoTemMeia(cartItems), [cartItems]);

  const podeSugerir = Boolean(lojaCarrinho) && !jaTemMeia && cartItems.length > 0;

  const excludeQuery = useMemo(() => {
    const refs = [...new Set(cartItems.map((item) => item?.use_referencia).filter(Boolean))];
    if (refs.length === 0) return "";
    return `&exclude=${refs.map(encodeURIComponent).join(",")}`;
  }, [cartItems]);

  const searchQuery = useMemo(() => {
    if (!searchDebounced) return "";
    return `&search=${encodeURIComponent(searchDebounced)}`;
  }, [searchDebounced]);

  useEffect(() => {
    if (!podeSugerir) {
      setProdutos([]);
      setOpenDialog(false);
      setLoading(false);
      setLoadingPrecos(false);
      setLoadingMore(false);
      setPage(1);
      setHasMore(false);
      setTotal(0);
      setSearchInput("");
      dismissedRef.current = false;
      return;
    }

    let cancelled = false;

    // Abre o modal imediatamente com skeleton, depois chama a API
    if (!dismissedRef.current) {
      setOpenDialog(true);
    }
    setLoading(true);
    setLoadingPrecos(false);
    setPage(1);
    setHasMore(false);
    setTotal(0);
    setProdutos([]);

    const carregar = async () => {
      try {
        const querys = `?loja=${encodeURIComponent(
          lojaCarrinho
        )}&page=1&pageSize=${PAGE_SIZE}${excludeQuery}${searchQuery}`;
        const res = await v2_actionSugestoesMeias(querys);
        if (cancelled) return;

        const lista = res.status === 200 ? res.data.produtos || [] : [];
        const listaBase = marcarCarregandoPreco(lista);

        setProdutos(listaBase);
        setHasMore(Boolean(res.data?.hasMore));
        setTotal(Number(res.data?.total) || lista.length);
        setPage(1);

        if (listaBase.length === 0) {
          return;
        }

        setLoadingPrecos(true);
        const comPrecos = await enriquecerComPrecos(listaBase, lojaCarrinho);
        if (!cancelled) {
          setProdutos(comPrecos);
        }
      } catch (err) {
        console.error("Erro ao carregar sugestões de meias:", err);
        if (!cancelled) setProdutos([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingPrecos(false);
        }
      }
    };

    carregar();
    return () => {
      cancelled = true;
    };
  }, [podeSugerir, lojaCarrinho, excludeQuery, searchQuery]);

  useEffect(() => {
    if (jaTemMeia) {
      setOpenDialog(false);
      setSearchInput("");
      dismissedRef.current = false;
    }
  }, [jaTemMeia]);

  const handleFecharDialog = () => {
    setOpenDialog(false);
    setSearchInput("");
    dismissedRef.current = true;
  };

  const handleCarregarMais = async () => {
    if (!lojaCarrinho || loadingMore || !hasMore) return;

    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const querys = `?loja=${encodeURIComponent(
        lojaCarrinho
      )}&page=${nextPage}&pageSize=${PAGE_SIZE}${excludeQuery}${searchQuery}`;
      const res = await v2_actionSugestoesMeias(querys);
      const lista = res.status === 200 ? res.data.produtos || [] : [];

      if (lista.length === 0) {
        setHasMore(false);
        return;
      }

      const listaBase = marcarCarregandoPreco(lista);
      setProdutos((prev) => [...prev, ...listaBase]);
      setPage(nextPage);
      setHasMore(Boolean(res.data?.hasMore));
      setTotal(Number(res.data?.total) || total);

      setLoadingPrecos(true);
      const comPrecos = await enriquecerComPrecos(listaBase, lojaCarrinho);
      setProdutos((prev) => {
        const mapaNovos = new Map(
          comPrecos.map((item) => {
            const p = item.produto || item;
            return [`${p.use_referencia}-${p.use_cod_cor_produto}`, item];
          })
        );
        return prev.map((item) => {
          const p = item.produto || item;
          const key = `${p.use_referencia}-${p.use_cod_cor_produto}`;
          return mapaNovos.get(key) || item;
        });
      });
    } catch (err) {
      console.error("Erro ao carregar mais meias:", err);
      toast.error("Não foi possível carregar mais opções.");
    } finally {
      setLoadingMore(false);
      setLoadingPrecos(false);
    }
  };

  const handleAdicionarMeia = async (item) => {
    const sugerido = item?.produto || item;
    if (!sugerido?.use_referencia || !lojaCarrinho || addingKey) return;

    const grades = Array.isArray(sugerido.grades) ? sugerido.grades.filter(Boolean) : [];
    const tamanho = grades[0];
    if (!tamanho) {
      toast.error("Esta meia não tem tamanho disponível nesta loja.");
      return;
    }

    const itemKey = `${sugerido.use_referencia}-${sugerido.use_cod_cor_produto}`;
    setAddingKey(itemKey);

    try {
      let product = sugerido;
      const resProduto = await v2_actionBuscarProduto(
        `?referencia=${sugerido.use_referencia}&cor_produto=${sugerido.use_cod_cor_produto}`
      );
      if (resProduto.status === 200 && resProduto.data.produto) {
        product = {
          ...resProduto.data.produto,
          grades: sugerido.grades,
          imagens: sugerido.imagens || resProduto.data.produto.imagens,
          imagens_miniaturas:
            sugerido.imagens_miniaturas || resProduto.data.produto.imagens_miniaturas,
          preco_loja: sugerido.preco_loja,
        };
      }

      let preco = sugerido.preco_loja;
      if (preco === undefined || preco === null || preco === "") {
        preco = await buscarPrecoLoja({
          referencia: product.use_referencia,
          corProduto: product.use_cod_cor_produto,
          tamanho,
          loja: lojaCarrinho,
        });
      }

      if (preco === undefined || preco === null || preco === "") {
        toast.error("Não foi possível obter o preço desta meia.");
        return;
      }

      const dims = getDimensoes(product);
      const filial = formatarCodigoFilial(lojaCarrinho);

      const cartItem = {
        product_image:
          product.images && product.images.length > 0 ? product.images[0]._id : null,
        product_id: product._id,
        variant_id: null,
        quantity: 1,
        discount_total: 0,
        name: product.name,
        regular_price: preco,
        price: preco,
        price_total: preco,
        sku: product.sku,
        tax_class: 0,
        tax_total: 0,
        produto: product.produto,
        cor_produto: product.use_cod_cor_produto || product.cor_produto,
        grade: tamanho,
        variant_name: null,
        weight: product.weight,
        dimension: product.dimension,
        images: product.images,
        imagens: product.imagens,
        imagens_miniaturas: product.imagens_miniaturas,
        filial,
        desc_cor_produto: product.desc_cor_produto,
        length: dims.length,
        heigth: dims.height,
        width: dims.width,
        use_descricao: product.use_descricao,
        use_referencia: product.use_referencia,
        use_cod_cor_produto: product.use_cod_cor_produto,
        use_cod_filial: product.use_cod_filial,
        use_cor_produto: product.use_cor_produto,
        grupo_produto: product.grupo_produto,
        subgrupo_produto: product.subgrupo_produto,
        linha: product.linha,
      };

      dispatch(addToCart(cartItem));
      dispatch({ type: Actions.CEP_ORIGEM_PRODUTO, cepOrigemProduto: lojaCarrinho });
      setOpenDialog(false);
      toast.success("Meia adicionada ao carrinho!");
    } catch (err) {
      console.error("Erro ao adicionar meia ao carrinho:", err);
      toast.error("Não foi possível adicionar a meia ao carrinho.");
    } finally {
      setAddingKey(null);
    }
  };

  if (!podeSugerir && !openDialog) {
    return null;
  }

  const skeletonCount = isCompact ? 4 : 8;

  return (
    <Dialog
      open={openDialog}
      onClose={handleFecharDialog}
      fullScreen={isSmallScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isSmallScreen ? 0 : 2.5,
          overflow: "hidden",
          m: isSmallScreen ? 0 : undefined,
          maxHeight: isSmallScreen ? "100%" : "90vh",
          height: isSmallScreen ? "100%" : "auto",
          display: "flex",
          flexDirection: "column",
        },
      }}
      aria-labelledby="sugestao-meias-title"
    >
      <DialogTitle
        id="sugestao-meias-title"
        sx={{
          pr: { xs: 5.5, sm: 6 },
          py: { xs: 1.5, sm: 2.25 },
          bgcolor: "#f8f9fb",
          borderBottom: "1px solid",
          borderColor: "grey.200",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={{ xs: 1, sm: 1.5 }}>
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              borderRadius: 1.5,
              bgcolor: BRAND,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FiGift size={isCompact ? 16 : 20} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1rem", sm: "1.15rem" },
                color: BRAND,
                lineHeight: 1.2,
              }}
            >
              Quer incluir meias?
            </Typography>
            <Typography
              sx={{
                mt: 0.4,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                color: "text.secondary",
                lineHeight: 1.35,
                display: { xs: "-webkit-box", sm: "block" },
                WebkitLineClamp: { xs: 2, sm: "unset" },
                WebkitBoxOrient: "vertical",
                overflow: { xs: "hidden", sm: "visible" },
              }}
            >
              {loading
                ? "Carregando opções de meias…"
                : isCompact
                  ? "Estoque na mesma loja do pedido."
                  : "Escolha uma opção com estoque na mesma loja do pedido."}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: { xs: 0.85, sm: 1.25 } }}
              flexWrap="wrap"
              useFlexGap
            >
              {lojaCarrinho && (
                <Chip
                  size="small"
                  label={`Loja ${formatarCodigoFilial(lojaCarrinho)}`}
                  sx={{
                    height: 24,
                    fontWeight: 600,
                    bgcolor: BRAND_LIGHT,
                    color: BRAND,
                  }}
                />
              )}
              {(loading || loadingPrecos) && (
                <Chip
                  size="small"
                  label={
                    loading
                      ? isCompact
                        ? "Carregando…"
                        : "Buscando meias…"
                      : isCompact
                        ? "Preços…"
                        : "Consultando preços…"
                  }
                  icon={<CircularProgress size={12} sx={{ color: `${BRAND} !important` }} />}
                  sx={{ height: 24, bgcolor: "#fff", border: "1px solid", borderColor: "grey.200" }}
                />
              )}
            </Stack>
          </Box>
        </Stack>
        <IconButton
          aria-label="fechar"
          onClick={handleFecharDialog}
          sx={{ position: "absolute", right: { xs: 6, sm: 10 }, top: { xs: 6, sm: 10 } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          bgcolor: "#f3f4f6",
          px: { xs: 1.25, sm: 2, md: 2.5 },
          py: { xs: 1.5, sm: 2, md: 2.5 },
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <TextField
          fullWidth
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Pesquisar por referência, marca ou descrição"
          disabled={loading && produtos.length === 0}
          sx={{
            mb: 2,
            bgcolor: "#fff",
            borderRadius: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: 1.5,
              "&.Mui-focused fieldset": { borderColor: BRAND },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FiSearch size={16} color="#6b7280" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="limpar pesquisa"
                  onClick={() => setSearchInput("")}
                  edge="end"
                >
                  <FiX size={16} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {loading && produtos.length === 0 ? (
          <Box
            sx={{
              display: "grid",
              gap: { xs: 1.25, sm: 1.5, md: 1.75 },
              gridTemplateColumns: isCompact
                ? "1fr"
                : {
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)",
                  },
            }}
          >
            {Array.from({ length: skeletonCount }).map((_, index) => (
              <MeiaCardSkeleton key={`skeleton-${index}`} compact={isCompact} />
            ))}
          </Box>
        ) : !loading && produtos.length === 0 ? (
          <Box
            sx={{
              py: { xs: 5, sm: 6 },
              px: 2,
              textAlign: "center",
              bgcolor: "#fff",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "grey.300",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: BRAND, mb: 0.5 }}>
              Nenhuma meia encontrada
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
              {searchDebounced
                ? `Sem resultados para “${searchDebounced}” nesta loja.`
                : "Não há meias com estoque nesta loja no momento."}
            </Typography>
            {searchDebounced && (
              <Button
                onClick={() => setSearchInput("")}
                sx={{ mt: 1.5, textTransform: "none", fontWeight: 600, color: BRAND }}
              >
                Limpar pesquisa
              </Button>
            )}
          </Box>
        ) : (
          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gap: { xs: 1.25, sm: 1.5, md: 1.75 },
                gridTemplateColumns: isCompact
                  ? "1fr"
                  : {
                      sm: "repeat(2, 1fr)",
                      md: "repeat(3, 1fr)",
                      lg: "repeat(4, 1fr)",
                    },
                opacity: loading ? 0.65 : 1,
                transition: "opacity 0.15s ease",
              }}
            >
              {produtos.map((item) => {
                const produto = item.produto || item;
                const key = `${produto.use_referencia}-${produto.use_cod_cor_produto}-${produto._id}`;
                return (
                  <MeiaCard
                    key={key}
                    item={item}
                    compact={isCompact}
                    addingKey={addingKey}
                    onAdicionar={handleAdicionarMeia}
                  />
                );
              })}
            </Box>

            {(hasMore || loadingMore) && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, pt: 0.5 }}>
                {total > 0 && (
                  <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                    Mostrando {produtos.length} de {total}
                  </Typography>
                )}
                <Button
                  onClick={handleCarregarMais}
                  disabled={loadingMore || !hasMore || Boolean(addingKey) || loading}
                  variant="outlined"
                  fullWidth={isCompact}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 1.5,
                    minHeight: 44,
                    px: 3,
                    maxWidth: isCompact ? "100%" : 280,
                    color: BRAND,
                    borderColor: "grey.300",
                    bgcolor: "#fff",
                    "&:hover": {
                      borderColor: BRAND,
                      bgcolor: BRAND_LIGHT,
                    },
                  }}
                >
                  {loadingMore ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={16} sx={{ color: BRAND }} />
                      <span>Carregando…</span>
                    </Stack>
                  ) : (
                    "Carregar mais meias"
                  )}
                </Button>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          bgcolor: "#fff",
          borderTop: "1px solid",
          borderColor: "grey.200",
          justifyContent: isCompact ? "stretch" : "space-between",
          flexDirection: isCompact ? "column" : "row",
          alignItems: isCompact ? "stretch" : "center",
          gap: { xs: 1, sm: 2 },
          flexShrink: 0,
          pb: { xs: "max(12px, env(safe-area-inset-bottom))", sm: 2 },
        }}
      >
        {!isCompact && (
          <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
            A meia entra no pedido com a mesma loja dos itens atuais.
          </Typography>
        )}
        <Button
          onClick={handleFecharDialog}
          variant={isCompact ? "outlined" : "text"}
          fullWidth={isCompact}
          disabled={Boolean(addingKey)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: isCompact ? BRAND : "text.secondary",
            borderRadius: 1.5,
            px: 1.5,
            minHeight: { xs: 44, sm: 36 },
            borderColor: isCompact ? "grey.300" : undefined,
            "&:hover": { bgcolor: "grey.100", color: BRAND },
          }}
        >
          Continuar sem meias
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SugestaoMeiasResumo;
